import { Component, EventEmitter, HostListener, Output } from '@angular/core'
import { ApiService } from 'src/app/services/api.service'
import { ApplicationType, SyncPhaseDto } from 'src/app/services/dtos'
import { UiService } from 'src/app/services/ui.service'
import { AccountService } from 'src/app/account/services/account.service'
import { Application, ApplicationProviderService } from 'src/app/account/services/application.service'
import { LaunchRestoreSettings, LaunchSyncDialogBase } from 'src/app/account/components/launch-sync-dialog-base'
import { AccessLevelService, AppPermission } from 'src/app/services/access-level.service'

@Component({
  selector: 'launch-recovery-dialog',
  templateUrl: './launch-recovery-dialog.component.html',
  styleUrls: ['./launch-recovery-dialog.component.scss'],
  standalone: false,
})
export class LaunchRecoveryDialogComponent extends LaunchSyncDialogBase {
  constructor(
    applicationProviderService: ApplicationProviderService,
    accountService: AccountService,
    apiService: ApiService,
    uiService: UiService,
    accessLevelService: AccessLevelService
  ) {
    super(applicationProviderService, accountService, apiService, uiService, accessLevelService)
  }

  @Output() launch = new EventEmitter<Array<LaunchRestoreSettings>>()

  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKeydown(event: KeyboardEvent): void {
    if (!this.isTopmostModal()) return
    if (this.launching) return
    event.preventDefault()
    this.onCancelClick()
  }

  private _quarantineRecoveryEnabled: boolean = false
  private _malwareScanEnabled: boolean = false

  ngOnInit() {
    super.ngOnInit()
    // Combined apps + RP picker is the new first step; override the base default
    // ('applications') so the wizard opens on the right pane.
    this.activeStepKey = 'app-recovery'
  }

  onLaunchClick() {
    this.launch.emit(
      this.selectedApplications.reduce((accum, app) => {
        let rp = this.computeApplicationRecoveryPoint(app)
        if (rp) {
          accum.push({
            application: app,
            recoveryPointId: rp.recoveryPointId,
            isRealTime: this.isRealTime && app.rpo === 0,
            networkSandboxEnabled: false,
            quarantineEnabled: this.quarantineRecoveryEnabled,
            malwareScanEnabled: this.malwareScanEnabled,
          } as LaunchRestoreSettings)
        }
        return accum
      }, [] as Array<LaunchRestoreSettings>)
    )
  }

  toggleQuarantineRecovery(event: any) {
    this._quarantineRecoveryEnabled = event.target.checked
    // Disable scan if quarantine is turned off

    if (!event.target.checked) {
      this._malwareScanEnabled = event.target.checked
    }
  }

  get quarantineRecoveryEnabled(): boolean {
    return this._quarantineRecoveryEnabled
  }

  // Turn GuardDuty scan on or off based on checkbox
  toggleMalwareScan(event: any) {
    this._malwareScanEnabled = event.target.checked
  }

  get malwareScanEnabled(): boolean {
    return this._malwareScanEnabled
  }

  get isAzure(): boolean {
    return this.application?.type === ApplicationType.AZURE
  }

  // Compute-instance noun for quarantine copy; differs by cloud.
  get quarantineInstanceNoun(): string {
    return this.isAzure ? 'Azure virtual machines' : 'EC2 instances'
  }

  // Quarantine is the only optional step between apps+RP and review. When the
  // account doesn't have it, skip straight to review so the wizard never
  // dead-ends on an absent step.
  get nextStepAfterAppRecovery(): string {
    return this.accountService.quarantineEnabled ? 'quarantine' : 'review'
  }

  // Selected apps that don't resolve to a usable RP at the current slider
  // position. Surfaced in the Review step so the user can see (and fix)
  // missing coverage before launching.
  get appsMissingRecoveryPoint(): Array<Application> {
    return this.selectedApplications.filter((app) => !this.computeApplicationRecoveryPoint(app))
  }

  protected applicationIsCandidate(application: Application) {
    return application.syncPhase === SyncPhaseDto.STANDBY
  }

  protected getRequiredPermission(): AppPermission {
    return AppPermission.MANAGE_RECOVERY
  }
}
